export default function Requirements() {
  const requirements = [
    {
      icon: 'ri-lock-line',
      title: 'Access Controls',
      description:
        'Implement role-based access controls to ensure only authorized personnel can access sensitive client information and privileged communications.',
      details: [
        'Multi-factor authentication for all users',
        'Role-based permissions for case files',
        'Privileged access management for partners',
        'Regular access reviews and audits',
      ],
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'Data Encryption',
      description:
        'Encrypt all client data both at rest and in transit to protect attorney-client privilege and confidential legal information.',
      details: [
        'End-to-end encryption for communications',
        'Encrypted storage for case documents',
        'Secure file sharing with clients',
        'Email encryption for privileged communications',
      ],
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Risk Assessments',
      description:
        'Conduct annual comprehensive risk assessments to identify vulnerabilities in your legal practice management systems.',
      details: [
        'Annual security vulnerability scans',
        'Practice management system audits',
        'Third-party vendor risk assessments',
        'Documented remediation plans',
      ],
    },
    {
      icon: 'ri-file-list-line',
      title: 'Audit Logging',
      description:
        'Maintain detailed audit trails of all access to client files and sensitive legal data for compliance verification.',
      details: [
        'Comprehensive access logging',
        'Document modification tracking',
        'User activity monitoring',
        'Tamper-proof log retention',
      ],
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Incident Response',
      description:
        'Establish documented incident response procedures to handle potential data breaches affecting client confidentiality.',
      details: [
        'Breach notification protocols',
        'Client communication procedures',
        'State Bar reporting compliance',
        '24/7 incident response team',
      ],
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Staff Training',
      description:
        'Provide regular cybersecurity awareness training for all attorneys and staff handling confidential client information.',
      details: [
        'Annual security awareness training',
        'Phishing simulation exercises',
        'Ethics and confidentiality training',
        'Documented training completion',
      ],
    },
  ];

  return (
    <section id="requirements" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <i className="ri-checkbox-circle-line text-[#e10708] text-xl mr-2 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-[#e10708] text-sm uppercase tracking-widest font-light">
              Compliance Requirements
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-serif text-[#1A1F2E] leading-tight mb-6">
            Key SB 2610 Requirements<br />
            for Law Firms
          </h2>

          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            Texas law firms must implement these six critical cybersecurity measures to protect client data and maintain compliance with state regulations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {requirements.map((req, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group"
            >
              <div className="w-16 h-16 bg-[#e10708]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#e10708] transition-colors duration-300">
                <i className={`${req.icon} text-[#e10708] text-3xl group-hover:text-white transition-colors duration-300 w-8 h-8 flex items-center justify-center`}></i>
              </div>

              <h3 className="text-xl font-bold text-[#1A1F2E] mb-3">
                {req.title}
              </h3>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                {req.description}
              </p>

              <ul className="space-y-2">
                {req.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start text-sm text-gray-600">
                    <i className="ri-check-line text-[#e10708] mr-2 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center"></i>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Tiered Requirements by Organization Size */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-[#1A1F2E] mb-4">Tiered Requirements by Organization Size</h3>
            <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
              The requirements scale with your organization size, making compliance achievable for organizations of all sizes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Tier 1: <20 Employees */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-[#0D9488]">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-line text-white text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h4 className="text-2xl font-bold text-[#1A1F2E] mb-2">&lt;20 Employees</h4>
                <p className="text-[#0D9488] text-sm font-semibold uppercase tracking-wider">Basic Safeguards</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0D9488] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Password policies</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0D9488] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Employee cybersecurity training</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0D9488] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Basic access controls</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#0D9488] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Data backup procedures</span>
                </li>
              </ul>
            </div>

            {/* Tier 2: 20-99 Employees */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-[#e10708]">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e10708] to-[#ff4444] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-white text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h4 className="text-2xl font-bold text-[#1A1F2E] mb-2">20–99 Employees</h4>
                <p className="text-[#e10708] text-sm font-semibold uppercase tracking-wider">Moderate Protections</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#e10708] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">CIS Controls Implementation Group 1</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#e10708] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Multi-factor authentication (MFA)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#e10708] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Security awareness training</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#e10708] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Incident response planning</span>
                </li>
              </ul>
            </div>

            {/* Tier 3: 100-249 Employees */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border-2 border-[#1A1F2E]">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1A1F2E] to-[#2A3142] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-line text-white text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h4 className="text-2xl font-bold text-[#1A1F2E] mb-2">100–249 Employees</h4>
                <p className="text-[#1A1F2E] text-sm font-semibold uppercase tracking-wider">Full Compliance</p>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#1A1F2E] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">NIST Cybersecurity Framework</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#1A1F2E] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">ISO/IEC 27001 compliance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#1A1F2E] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">CIS Controls or SOC 2</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#1A1F2E] mr-2 mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                  <span className="text-gray-600 text-sm">Comprehensive security program</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mt-16 bg-gradient-to-r from-[#1A1F2E] to-[#2A3142] rounded-2xl p-12 text-center">
          <h3 className="text-3xl font-serif text-white mb-4">
            Not Sure Where to Start?
          </h3>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Our legal IT specialists will conduct a comprehensive compliance assessment and provide a detailed roadmap to meet all SB 2610 requirements.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-[#e10708] text-white px-8 py-4 rounded-full text-base font-bold hover:bg-[#c10607] hover:shadow-xl hover:shadow-[#e10708]/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Schedule Free Assessment
          </button>
        </div>
      </div>
    </section>
  );
}